import { CategoryNode } from '../../services/api'

/** Find the path (root → node) to the category with the given id, or null. */
export function findCategoryPath(nodes: CategoryNode[], id: string): CategoryNode[] | null {
  for (const node of nodes) {
    if (node.id === id) return [node]
    if (node.children && node.children.length > 0) {
      const childPath = findCategoryPath(node.children, id)
      if (childPath) return [node, ...childPath]
    }
  }
  return null
}

interface Props {
  categories: CategoryNode[]
  selectedPath: CategoryNode[]
  onSelectCategory: (path: CategoryNode[]) => void
  attributes: Record<string, any>
  onAttributesChange: (attrs: Record<string, any>) => void
  /** Optional label text for the picker heading. */
  label?: string
}

/**
 * Hierarchical (up to 3-level) category drill-down picker with per-category
 * spec/attribute fields. Selecting a category clears attribute values so stale
 * specs from a previously-selected category never leak into the payload.
 * Shared by the create (ProductForm) and edit (ProductEdit) flows.
 */
export default function CategoryAttributePicker({
  categories,
  selectedPath,
  onSelectCategory,
  attributes,
  onAttributesChange,
  label = 'Category',
}: Props) {
  if (categories.length === 0) return null

  const setAttr = (key: string, value: any) => {
    const next = { ...attributes }
    if (value === undefined || value === '' || value === false) {
      delete next[key]
    } else {
      next[key] = value
    }
    onAttributesChange(next)
  }

  const selectCategory = (path: CategoryNode[]) => {
    // Clear attribute values when the category changes.
    onAttributesChange({})
    onSelectCategory(path)
  }

  const leafCategory = selectedPath[selectedPath.length - 1]

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
      <label className="block text-sm font-medium text-gray-700 mb-3">
        {label} <span className="text-gray-400 font-normal">— Optional</span>
      </label>

      {/* Level 1: Top categories */}
      <div className="flex flex-wrap gap-2 mb-3">
        {categories.map(cat => {
          const isActive = selectedPath[0]?.id === cat.id
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => selectCategory([cat])}
              className={`px-3 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-1.5
                ${isActive
                  ? 'bg-blue-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              <span>{cat.icon || '📦'}</span>
              <span>{cat.name}</span>
            </button>
          )
        })}
      </div>

      {/* Level 2: Subcategories of selected parent */}
      {selectedPath[0]?.children && selectedPath[0].children.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3 pl-2 border-l-2 border-blue-200">
          {selectedPath[0].children.map(child => {
            const isActive = selectedPath[1]?.id === child.id
            return (
              <button
                key={child.id}
                type="button"
                onClick={() => selectCategory([selectedPath[0], child])}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all
                  ${isActive
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {child.name}
              </button>
            )
          })}
        </div>
      )}

      {/* Level 3: Leaf subcategories */}
      {selectedPath[1]?.children && selectedPath[1].children.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3 pl-4 border-l-2 border-blue-200">
          {selectedPath[1].children.map(leaf => {
            const isActive = selectedPath[2]?.id === leaf.id
            return (
              <button
                key={leaf.id}
                type="button"
                onClick={() => selectCategory([selectedPath[0], selectedPath[1], leaf])}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all
                  ${isActive
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {leaf.name}
              </button>
            )
          })}
        </div>
      )}

      {/* Product attributes for the selected category */}
      {leafCategory?.attributes?.length ? (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
            Specifications
          </p>
          {(leafCategory.attributes || []).map(attr => {
            const val = attributes[attr.key]
            const label = typeof attr.label === 'object' ? attr.label.en : (attr.label || attr.key)

            return (
              <div key={attr.key}>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {label}{attr.unit ? ` (${attr.unit})` : ''}
                </label>

                {attr.type === 'enum' && attr.options ? (
                  <div className="flex flex-wrap gap-1.5">
                    {attr.options.map(opt => {
                      const isActive = val === opt.v
                      return (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => setAttr(attr.key, isActive ? undefined : opt.v)}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            isActive
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {opt.en || opt.label || opt.v}
                        </button>
                      )
                    })}
                  </div>
                ) : attr.type === 'bool' ? (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!val}
                      onChange={e => setAttr(attr.key, e.target.checked || undefined)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-500"
                    />
                    <span className="text-sm text-gray-700">{label}</span>
                  </label>
                ) : (
                  <input
                    type={attr.type === 'int' || attr.type === 'decimal' ? 'number' : 'text'}
                    value={val ?? ''}
                    onChange={e => setAttr(attr.key, e.target.value || undefined)}
                    placeholder={`Enter ${label.toLowerCase()}`}
                    step={attr.type === 'decimal' ? '0.1' : '1'}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm
                      focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                )}
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
